import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  CheckboxField,
  ConfirmationPanel,
  ConnectionStatus,
  Dialog,
  DistributionProgress,
  EmptyState,
  FilterChips,
  PageTitle,
  RoomGrid,
  Spinner,
  TEXT_LINK,
  useToast,
} from '@rooming/ui';
import {
  formatInZone,
  isProblem,
  missingChips,
  mySlotId,
  useLiveBoard,
  type BoardRoom,
  type BoardSlot,
} from '@rooming/web-core';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, passengerApi, type SwapRequest } from '../api';
import { InvitationSection } from './InvitationSection';
import { RosterPanel } from './RosterPanel';
import { messageOf } from '../messages';
import { useSession } from '../session';
import { SwapPanel } from './SwapPanel';

/** Problems after which the screen must re-read the board and my slot (someone else changed things first). */
/** A PDF or an image opens in the browser; a Word document is downloaded. */
const opensInBrowser = (type: string): boolean => type === 'application/pdf' || type.startsWith('image/');

const STALE_SLUGS = new Set([
  'plaza-ocupada',
  'modificacion-concurrente',
  'estado-desactualizado',
  'seleccion-cerrada',
  'ya-tiene-plaza',
  'intercambio-pendiente',
  'intercambio-no-vigente',
  'intercambio-invalidado',
  'recurso-no-encontrado',
  'umbral-no-alcanzado',
  'transicion-invalida',
  'distribucion-confirmada',
]);

type Pending =
  | { readonly kind: 'choose'; readonly room: BoardRoom; readonly slot: BoardSlot; readonly from?: BoardRoom }
  | { readonly kind: 'release'; readonly room: BoardRoom }
  | { readonly kind: 'swap'; readonly room: BoardRoom; readonly slot: BoardSlot; readonly from: BoardRoom }
  // What the signer saw when they pressed the button: that exact version is what gets signed.
  | { readonly kind: 'confirm'; readonly version: number; readonly assigned: number; readonly passengers: number };

const TITLES: Record<Pending['kind'] | 'change' | 'request', string> = {
  choose: '¿Reservar esta plaza?',
  request: 'Pedir esta plaza',
  change: '¿Cambiar de habitación?',
  release: '¿Liberar tu plaza?',
  swap: '¿Pedir el intercambio?',
  confirm: '¿Confirmar la distribución?',
};
const CONFIRM: Record<Pending['kind'] | 'change' | 'request', string> = {
  choose: 'Reservar',
  request: 'Enviar solicitud',
  change: 'Cambiar',
  release: 'Liberar',
  swap: 'Pedir intercambio',
  confirm: 'Confirmar distribución',
};

/**
 * The room board of one stay (USER_FLOWS.md F-P2..F-P5). Passengers choose, change (confirmation modal, Q-DOM-16),
 * release their slot or ask whoever holds another slot to swap (both must agree, DR-113); guardians see the same board
 * read-only with the occupants. Live over SSE; the database decides: a click is only a request, and a conflict
 * re-reads the board instead of guessing.
 */
export function BoardPage() {
  const { stayId = '' } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { signOut } = useSession();
  const [pending, setPending] = useState<Pending>();
  // The list of places the passenger may ask to exchange with (owner, 2026-10-06): no need to find and tap one.
  const [picking, setPicking] = useState(false);
  const [roomFilter, setRoomFilter] = useState<'all' | 'free' | 'full'>('all');
  const [busy, setBusy] = useState(false);
  // The checkbox of the hotel's terms and conditions (owner, 2026-10-20), ticked inside the "choose" dialog.
  const [termsTicked, setTermsTicked] = useState(false);

  const mineKey = ['my-assignment', stayId] as const;
  const swapsKey = ['swaps', stayId] as const;
  const progressKey = ['distribution', stayId] as const;
  const placementKey = ['placement', stayId] as const;
  const me = useQuery({ queryKey: ['me'], queryFn: async () => (await passengerApi.me()).data, staleTime: Infinity });
  const isGuardian = me.data?.kind === 'APODERADO';
  const isPassenger = me.data !== undefined && !isGuardian;
  const { board, connection, resync } = useLiveBoard(api, stayId, {
    alsoInvalidate: [mineKey, swapsKey, progressKey, placementKey],
    // A guardian follows the whole group: every change moves the progress, not only "my" slot.
    invalidateOnAnyChange: isGuardian,
    onSessionEnded: () => void signOut(),
    onSwapsChanged: () => {
      void queryClient.invalidateQueries({ queryKey: ['swaps', stayId] });
      // An administrator decided a request for a bed (approved: the slot also changes; rejected: only this fires).
      void queryClient.invalidateQueries({ queryKey: placementKey });
    },
  });
  const mine = useQuery({
    queryKey: mineKey,
    queryFn: async () => {
      const response = await passengerApi.myAssignment(stayId);
      return { slotId: response.data.slotId, etag: response.etag };
    },
    enabled: isPassenger,
  });
  // Guardians see how many passengers of the group already chose, and who is missing (matrix §3.1).
  const progress = useQuery({
    queryKey: progressKey,
    queryFn: async () => (await passengerApi.distribution(stayId)).data,
    enabled: isGuardian,
  });
  const swaps = useQuery({
    queryKey: swapsKey,
    queryFn: async () => (await passengerApi.swaps(stayId)).data,
    enabled: isPassenger,
  });
  // A bed in a women's or men's room asked by someone with undefined sex waits for an administrator (owner, 2026-10-20).
  const placement = useQuery({
    queryKey: placementKey,
    queryFn: async () => (await passengerApi.myPlacementRequest(stayId)).data.item,
    enabled: isPassenger,
  });
  // The hotel's time zone for the expiry of a swap request (the stays list is already cached from the home screen).
  const stays = useQuery({ queryKey: ['my-stays'], queryFn: async () => (await passengerApi.myStays()).data.items });
  const hotel = stays.data?.find((stay) => stay.id === stayId)?.hotel ?? null;
  const timeZone = hotel?.timezone ?? 'UTC';

  /** Runs one command: success message, or the server's reason — and a re-read when the screen was stale. */
  async function run(
    work: () => Promise<unknown>,
    done: string | ((result: unknown) => string),
    staleText?: string,
  ): Promise<void> {
    setBusy(true);
    try {
      const result = await work();
      toast('success', typeof done === 'function' ? done(result) : done);
    } catch (problem) {
      toast(
        'error',
        staleText !== undefined && isProblem(problem, 'estado-desactualizado') ? staleText : messageOf(problem),
      );
      if (!(isProblem(problem) && STALE_SLUGS.has(problem.slug))) return;
    } finally {
      setBusy(false);
      setPending(undefined);
      setTermsTicked(false);
    }
    resync();
    void queryClient.invalidateQueries({ queryKey: placementKey });
  }

  function confirm(): void {
    if (pending === undefined) return;
    if (pending.kind === 'choose') {
      const etag = mine.data?.etag;
      const changing = pending.from !== undefined;
      // The hotel's terms: sent only when the passenger ticked the checkbox (the server checks the version).
      const seenTerms = board.data?.hotelTerms ?? null;
      const termsToAccept = seenTerms !== null && !seenTerms.accepted ? seenTerms.version : undefined;
      void run(
        () =>
          passengerApi.choose(
            stayId,
            pending.slot.id,
            changing,
            changing && etag !== undefined ? { ifMatch: etag } : { ifNoneMatch: '*' },
            termsToAccept,
          ),
        (result) =>
          (result as { data: { pendingRequest?: unknown } }).data.pendingRequest !== undefined
            ? 'Solicitud enviada. Funtour se va a comunicar con tu familia y te avisa apenas la resuelva.'
            : changing
              ? `Te cambiaste a ${pending.room.label}.`
              : `Reservaste una plaza en ${pending.room.label}.`,
      );
    } else if (pending.kind === 'release') {
      void run(() => passengerApi.release(stayId, mine.data?.etag), 'Liberaste tu plaza.');
    } else if (pending.kind === 'confirm') {
      void run(
        () => passengerApi.confirmDistribution(stayId, pending.version),
        'Confirmaste la distribución.',
        'La distribución cambió mientras la revisabas. Mirala de nuevo y confirmá.',
      ).then(() => queryClient.invalidateQueries({ queryKey: progressKey }));
    } else {
      void run(() => passengerApi.askSwap(stayId, pending.slot.id), 'Solicitud enviada. Te avisamos cuando respondan.');
    }
  }

  function answer(swap: SwapRequest, kind: 'acceptance' | 'rejection' | 'cancellation'): void {
    const done =
      kind === 'acceptance'
        ? `Intercambio realizado: ahora estás en ${swap.otherRoom}.`
        : kind === 'rejection'
          ? 'Rechazaste el intercambio.'
          : 'Cancelaste tu solicitud.';
    void run(() => passengerApi.answerSwap(swap.id, kind), done);
  }

  if (board.isPending || me.isPending) return <Spinner />;
  if (board.isError)
    return (
      <Alert tone="error" title="No pudimos cargar las habitaciones">
        <p>{messageOf(board.error)}</p>
        <Button variant="secondary" className="mt-3" onClick={resync}>
          Reintentar
        </Button>
      </Alert>
    );

  const data = board.data;
  const terms = data.hotelTerms;
  const needsTerms = terms !== null && !terms.accepted;
  const mySlot = mySlotId(data);
  const myRoom = data.rooms.find((room) => room.slots.some((slot) => slot.id === mySlot));
  const confirmed = data.status === 'CONFIRMED';
  // Before confirmation: choose, change and release freely. Once confirmed (prompt10 §2.1, §7): whoever has no slot may
  // still take a free one; whoever has one moves only by an agreed exchange.
  const canChooseFree =
    !isGuardian && mine.isSuccess && (data.selectionOpen || (confirmed && data.windowOpen && myRoom === undefined));
  const canRelease = !isGuardian && mine.isSuccess && data.selectionOpen;
  const hasPendingRequest =
    swaps.data?.items.some((s) => s.status === 'REQUESTED' && s.direction === 'OUTGOING') === true;
  // Asking for a swap needs a slot of my own, swaps enabled by the tenant, and no other request of mine waiting.
  const canSwap =
    !isGuardian &&
    mine.isSuccess &&
    data.windowOpen &&
    swaps.data?.enabled === true &&
    myRoom !== undefined &&
    !hasPendingRequest;
  // Occupied places of OTHER rooms the passenger may use (the board already shows only the rooms of their own sex;
  // someone with undefined sex sees them all). The server still decides whether each exchange is possible.
  const swapOptions = canSwap
    ? data.rooms
        // An exchange never moves undefined sex into a women's or men's room: an administrator decides that.
        .filter((room) => room.bookable && !room.needsApproval && room.id !== myRoom.id)
        .flatMap((room) => room.slots.filter((slot) => slot.state === 'OCCUPIED').map((slot) => ({ room, slot })))
    : [];
  const freeIn = (room: BoardRoom): number => room.slots.filter((slot) => slot.state === 'FREE').length;
  const shownRooms = data.rooms.filter(
    (room) => roomFilter === 'all' || (roomFilter === 'free' ? freeIn(room) > 0 : freeIn(room) === 0),
  );
  const dialogKind =
    pending?.kind === 'choose' && pending.room.needsApproval
      ? 'request'
      : pending?.kind === 'choose' && pending.from !== undefined
        ? 'change'
        : pending?.kind;
  const request = placement.data;
  const confirmation = progress.data?.confirmation;

  return (
    <>
      <div className="mb-3">
        <Link to="/" className={TEXT_LINK}>
          ← Mis estadías
        </Link>
      </div>
      {/* The dark opening panel of the design baseline: where you are, and your own place. */}
      {isGuardian ? (
        // The signatory's screen is a working view (owner, 2026-10-07): a plain title, no large banner.
        <PageTitle eyebrow="Vista firmante" subtitle={hotel === null ? undefined : `${hotel.name} · ${hotel.city}`}>
          Habitaciones
        </PageTitle>
      ) : (
        <section className="mb-6 rounded-3xl border border-hero-line bg-hero p-6 text-hero-text sm:p-8">
          {hotel !== null && (
            <p className="text-sm font-semibold text-hero-text">
              {hotel.name} · {hotel.city}
            </p>
          )}
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Habitaciones</h1>
          <p className="mt-2 text-base text-hero-text">
            Tu aventura empieza con una buena <em className="font-semibold">habitación.</em>
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm text-hero-text">Mi habitación asignada</p>
              <p className="text-lg font-bold" aria-live="polite">
                {myRoom === undefined ? (
                  'Todavía no elegiste plaza.'
                ) : (
                  <>
                    Tu plaza: <strong>{myRoom.label}</strong>
                  </>
                )}
              </p>
            </div>
            {myRoom !== undefined && (canRelease || canSwap) && (
              <div className="flex flex-wrap gap-2">
                {canSwap && (
                  <Button
                    variant="onHero"
                    onClick={() => {
                      setPicking(true);
                    }}
                  >
                    Realizar intercambio
                  </Button>
                )}
                {canRelease && (
                  <Button
                    variant="onHero"
                    onClick={() => {
                      setPending({ kind: 'release', room: myRoom });
                    }}
                  >
                    Liberar mi cupo
                  </Button>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      <div className="mb-5 flex flex-col gap-3">
        {confirmed && !isGuardian && (
          <Alert tone="success" title="Distribución confirmada">
            {myRoom === undefined
              ? 'La distribución ya fue confirmada, pero todavía podés elegir una de las plazas libres.'
              : 'La distribución ya fue confirmada. Para cambiar de habitación, pedile un intercambio a quien tenga la plaza que querés.'}
          </Alert>
        )}
        {isPassenger && myRoom === undefined && data.rooms.length > 0 && data.rooms.every((r) => !r.bookable) && (
          // Every room is full or none admits this passenger: they keep seeing the rooms, are told why they cannot
          // choose, and appear as "Sin plaza" to the signatory and the hotel.
          <Alert tone="info" title="Sin plaza por ahora">
            No hay habitaciones que puedas elegir desde la aplicación. Funtour se va a comunicar con vos para tu plaza.
          </Alert>
        )}
        {!data.selectionOpen && data.status !== 'CONFIRMED' && (
          <Alert tone="warning" title="Selección cerrada">
            {isGuardian
              ? 'La elección de habitaciones no está abierta en este momento.'
              : 'Por ahora no podés elegir ni cambiar tu plaza.'}
          </Alert>
        )}
        {isGuardian && (
          <Alert tone="info">
            {progress.data?.viewerIsSigner === true
              ? 'Sos el firmante del grupo: ves a todos los pasajeros y su habitación, y confirmás la distribución cuando esté lista. No elegís plazas.'
              : 'Vista de apoderado: podés ver la distribución, no elegir plazas ni confirmarla.'}
          </Alert>
        )}
        {isGuardian && progress.data !== undefined && (
          <DistributionProgress
            assigned={progress.data.assigned}
            passengers={progress.data.passengers}
            percent={progress.data.percentAssigned}
            freeSlots={progress.data.freeSlots}
            missing={missingChips(progress.data)}
          />
        )}
        {isGuardian && progress.data !== undefined && data.status !== 'DRAFT' && data.status !== 'CLOSED' && (
          <ConfirmationPanel
            confirmedText={
              confirmation === null || confirmation === undefined
                ? undefined
                : `Confirmada el ${formatInZone(confirmation.confirmedAt, timeZone)} con ${String(confirmation.assigned)} de ${String(confirmation.eligible)} pasajeros con plaza.`
            }
            thresholdPercent={progress.data.thresholdPercent}
            percent={progress.data.percentAssigned}
            confirmable={progress.data.confirmable}
            viewerIsSigner={progress.data.viewerIsSigner}
            action={
              progress.data.viewerIsSigner && progress.data.confirmable ? (
                <Button
                  onClick={() => {
                    const seen = progress.data;
                    setPending({
                      kind: 'confirm',
                      version: seen.version,
                      assigned: seen.assigned,
                      passengers: seen.passengers,
                    });
                  }}
                >
                  Confirmar distribución
                </Button>
              ) : undefined
            }
          />
        )}
        {isGuardian && progress.data?.viewerIsSigner === true && <InvitationSection />}
        {isGuardian && progress.data?.viewerIsSigner === true && <RosterPanel stayId={stayId} />}
        {isPassenger && request?.status === 'PENDING' && (
          <Alert tone="info" title="Tu solicitud está en revisión">
            <p>
              Pediste una plaza en <strong>{request.roomLabel}</strong>. Te la reservamos: para los demás figura
              ocupada. Un administrador de Funtour se va a comunicar con tu familia para confirmarla; hasta entonces
              todavía no es tuya.
            </p>
            <Button
              variant="secondary"
              className="mt-3"
              loading={busy}
              onClick={() => {
                void run(() => passengerApi.cancelPlacementRequest(stayId), 'Cancelaste tu solicitud.');
              }}
            >
              Cancelar solicitud
            </Button>
          </Alert>
        )}
        {isPassenger && request?.status === 'REJECTED' && (
          <Alert tone="warning" title="Tu solicitud no fue aprobada">
            Funtour no aprobó tu solicitud de plaza en {request.roomLabel}. Se va a comunicar con tu familia para
            resolver tu habitación.
          </Alert>
        )}
        {isPassenger && <SwapPanel swaps={swaps.data?.items ?? []} busy={busy} timeZone={timeZone} onAnswer={answer} />}
        {canSwap && (
          <p className="text-sm text-muted">
            ¿Querés cambiar con otra persona? Usá «Realizar intercambio» (o tocá la plaza ocupada que querés).
          </p>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-brand-700">
            {isGuardian
              ? 'Vista firmante'
              : data.viewerRoomAudience === 'COMPANION'
                ? 'Vista acompañante'
                : 'Vista pasajero'}
          </p>
          <h2 className="text-2xl font-bold">{isGuardian ? 'Distribución del grupo' : 'Elegí tu habitación'}</h2>
        </div>
        <ConnectionStatus state={connection} />
      </div>
      {data.rooms.length === 0 ? (
        <EmptyState title="No hay habitaciones para mostrar">
          Tu coordinador todavía no cargó las habitaciones.
        </EmptyState>
      ) : (
        <>
          <div className="mb-5">
            <FilterChips
              label="Filtrar habitaciones"
              value={roomFilter}
              onChange={setRoomFilter}
              options={[
                ['all', 'Todas las habitaciones'],
                ['free', 'Con lugares libres'],
                ['full', 'Completas'],
              ]}
            />
          </div>
          {shownRooms.length === 0 ? (
            <EmptyState title="Ninguna habitación coincide con ese filtro" />
          ) : (
            <div className="flex flex-col gap-6">
              {(
                [
                  ['PASSENGER', 'Habitaciones de pasajeros'],
                  ['COMPANION', 'Habitaciones de acompañantes'],
                ] as const
              ).map(([audience, title]) => {
                const list = shownRooms.filter((room) => (room.audience ?? 'PASSENGER') === audience);
                if (list.length === 0) return null;
                return (
                  <section key={audience} aria-labelledby={`block-${audience}`} className="flex flex-col gap-3">
                    {/* Two blocks only for whoever sees both (the signer): each member sees just their own rooms. */}
                    {data.rooms.some((room) => room.audience === 'COMPANION') &&
                    data.rooms.some((room) => (room.audience ?? 'PASSENGER') === 'PASSENGER') ? (
                      <h3 id={`block-${audience}`} className="text-lg font-bold">
                        {title}
                      </h3>
                    ) : null}
                    <RoomGrid
                      rooms={list}
                      slotAction={(room, slot) => {
                        if (isGuardian || !room.bookable) return undefined;
                        if (slot.state === 'FREE' && canChooseFree) {
                          return {
                            verb: 'Elegir',
                            text: '+ Elegir cama',
                            run: () => {
                              setPending({
                                kind: 'choose',
                                room,
                                slot,
                                ...(myRoom === undefined ? {} : { from: myRoom }),
                              });
                            },
                          };
                        }
                        // Another room's occupied slot: ask its holder to swap (same room = nothing to exchange).
                        if (slot.state === 'OCCUPIED' && canSwap && !room.needsApproval && room.id !== myRoom.id) {
                          return {
                            verb: 'Pedir intercambio',
                            run: () => {
                              setPending({ kind: 'swap', room, slot, from: myRoom });
                            },
                          };
                        }
                        return undefined;
                      }}
                    />
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}

      <Dialog
        open={picking}
        title="¿Con qué plaza querés intercambiar?"
        onClose={() => {
          setPicking(false);
        }}
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              setPicking(false);
            }}
          >
            Cancelar
          </Button>
        }
      >
        {swapOptions.length === 0 ? (
          <p>Todavía no hay plazas ocupadas en otras habitaciones para intercambiar.</p>
        ) : (
          <>
            <p className="mb-3">
              Elegí la plaza que querés. Le proponemos el cambio a quien la ocupa: solo se hace si acepta.
            </p>
            <ul className="flex flex-col gap-2">
              {swapOptions.map(({ room, slot }) => (
                <li key={slot.id}>
                  <Button
                    variant="secondary"
                    className="w-full justify-start"
                    onClick={() => {
                      setPicking(false);
                      if (myRoom !== undefined) setPending({ kind: 'swap', room, slot, from: myRoom });
                    }}
                  >
                    {room.label} · plaza {slot.position}
                    {slot.occupant === undefined ? '' : ` (${slot.occupant.firstNames} ${slot.occupant.lastNames})`}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
      </Dialog>

      <Dialog
        open={pending !== undefined}
        title={dialogKind === undefined ? '' : TITLES[dialogKind]}
        onClose={() => {
          if (!busy) {
            setPending(undefined);
            setTermsTicked(false);
          }
        }}
        actions={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setPending(undefined);
                setTermsTicked(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant={pending?.kind === 'release' ? 'danger' : 'primary'}
              loading={busy}
              disabled={pending?.kind === 'choose' && needsTerms && !termsTicked}
              onClick={confirm}
            >
              {dialogKind === undefined ? '' : CONFIRM[dialogKind]}
            </Button>
          </>
        }
      >
        {pending?.kind === 'release' && (
          <p>Vas a dejar tu plaza en {pending.room.label}. Otra persona podrá ocuparla.</p>
        )}
        {pending?.kind === 'choose' && pending.room.needsApproval && (
          <p>
            Tu sexo figura como «sin definir», así que no podés tomar esta plaza vos solo: le avisamos a Funtour, que se
            comunica con tu familia y la aprueba. Hasta que la apruebe, la plaza en {pending.room.label} queda reservada
            «en espera» (los demás la ven ocupada).
          </p>
        )}
        {pending?.kind === 'choose' && !pending.room.needsApproval && pending.from === undefined && (
          <p>Vas a reservar una plaza en {pending.room.label}.</p>
        )}
        {pending?.kind === 'choose' && !pending.room.needsApproval && pending.from !== undefined && (
          <p>
            Se liberará tu plaza en {pending.from.label} y pasarás a {pending.room.label}. Si la nueva plaza se ocupa
            antes, te quedás donde estás.
          </p>
        )}
        {pending?.kind === 'choose' && needsTerms && (
          // Only when the hotel loaded terms and conditions: no terms, no checkbox.
          <div className="mt-4 flex flex-col gap-3">
            {terms.file !== null && (
              <p className="text-base break-words">
                <a
                  href={`/api/v1/stays/${stayId}/hotel-terms/file`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={TEXT_LINK}
                >
                  {opensInBrowser(terms.file.type) ? 'Ver' : 'Descargar'} el documento de términos ({terms.file.name})
                </a>
              </p>
            )}
            {terms.text !== null && (
              <div
                tabIndex={0}
                role="region"
                aria-label="Términos y condiciones del hotel"
                className="max-h-48 overflow-y-auto rounded-xl border border-line bg-surface p-3 text-sm whitespace-pre-wrap"
              >
                {terms.text}
              </div>
            )}
            <CheckboxField
              checked={termsTicked}
              onChange={(event) => {
                setTermsTicked(event.target.checked);
              }}
            >
              Leí y acepto los{' '}
              {terms.url === null ? (
                'términos y condiciones del hotel'
              ) : (
                <a href={terms.url} target="_blank" rel="noopener noreferrer" className={TEXT_LINK}>
                  términos y condiciones del hotel
                </a>
              )}
              .
            </CheckboxField>
          </div>
        )}
        {pending?.kind === 'confirm' && (
          <p>
            Vas a confirmar la distribución tal como la ves ahora: {pending.assigned} de {pending.passengers} pasajeros
            con plaza. Después, quien ya tiene plaza solo cambia por un intercambio acordado con otro pasajero; quien
            todavía no tiene puede elegir una de las plazas libres. Solo un administrador de Funtour puede reabrirla.
          </p>
        )}
        {pending?.kind === 'swap' && (
          <p>
            Le vamos a proponer a quien ocupa esa plaza de {pending.room.label} intercambiarla por la tuya de{' '}
            {pending.from.label}. Solo se hace si la otra persona acepta; mientras tanto, cada uno sigue donde está.
          </p>
        )}
      </Dialog>
    </>
  );
}
